import React, { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { useStore } from "../context/StoreContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CheckoutSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("polling");
  const [paymentStatus, setPaymentStatus] = useState("");
  const { clearCart } = useStore();
  const attemptsRef = useRef(0);
  const clearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);
        setPaymentStatus(data.payment_status);
        if (data.payment_status === "paid") {
          setStatus("paid");
          if (!clearedRef.current) {
            clearedRef.current = true;
            clearCart();
          }
          return;
        }
        if (data.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= 8) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, 2000);
      } catch (err) {
        attemptsRef.current += 1;
        if (attemptsRef.current >= 5) {
          setStatus("error");
          return;
        }
        setTimeout(poll, 2000);
      }
    };
    poll();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line
  }, [sessionId]);

  return (
    <div data-testid="page-checkout-success" className="pt-[140px] pb-24 px-4 min-h-[70vh]">
      <div className="max-w-2xl mx-auto text-center">
        {status === "polling" && (
          <>
            <p className="font-mono text-xs uppercase tracking-widest text-ink/60 mb-4">
              CONFIRMING PAYMENT...
            </p>
            <h1 className="font-display uppercase text-5xl md:text-7xl">Hold tight.</h1>
            <p className="font-body text-lg mt-4 text-ink/80">
              We're checking your payment with Stripe. This usually takes a few seconds.
            </p>
          </>
        )}
        {status === "paid" && (
          <>
            <p className="font-mono text-xs uppercase tracking-widest text-dc-red mb-4">
              PAYMENT CONFIRMED
            </p>
            <h1 className="font-display uppercase text-5xl md:text-7xl">You've damaged the culture.</h1>
            <p className="font-body text-lg md:text-xl mt-4 text-ink/85">
              Your order is in. We'll email you tracking details shortly. Each piece is made and shipped by hand, so allow us the time it takes.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/shop" className="btn-ghost" data-testid="success-shop-more">
                Shop more
              </Link>
              <Link to="/" className="btn-solid" data-testid="success-home">
                Back home
              </Link>
            </div>
          </>
        )}
        {(status === "expired" || status === "timeout" || status === "error") && (
          <>
            <p className="font-mono text-xs uppercase tracking-widest text-dc-red mb-4">
              SOMETHING WENT WRONG ({status.toUpperCase()})
            </p>
            <h1 className="font-display uppercase text-4xl md:text-6xl">
              Payment not confirmed.
            </h1>
            <p className="font-body text-lg mt-4 text-ink/80">
              {paymentStatus ? `Last seen status: ${paymentStatus}. ` : ""}
              If you were charged but see this page, email hello@damageculture.in with your order details.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <Link to="/cart" className="btn-ghost">
                Back to cart
              </Link>
              <Link to="/contact" className="btn-solid">
                Contact us
              </Link>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CheckoutSuccessPage;
